import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bot, Sun, Moon, Sunrise, TrendingUp, TrendingDown,
  Package, Users, AlertTriangle, CheckCircle,
  Info, Sparkles, RefreshCw, ChevronRight, X, Clock,
  Cloud, CloudRain, CloudSnow, Thermometer, Calendar,
  ExternalLink, Lightbulb
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Severity Farben
const SEVERITY_STYLES = {
  critical: 'bg-red-500/10 border-red-500/30',
  warning: 'bg-amber-500/10 border-amber-500/30',
  success: 'bg-emerald-500/10 border-emerald-500/30',
  info: 'bg-blue-500/10 border-blue-500/30'
};

const SEVERITY_BADGE_STYLES = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  success: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

export default function Tagesassistent() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchAssistant = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/tagesassistent/`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Tagesassistent fetch error:', error);
      toast.error('Fehler beim Laden des Tagesassistenten');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const dismissInsight = async (insightId) => {
    try {
      const response = await fetch(`${API_BASE}/api/tagesassistent/dismiss/${insightId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setDismissedIds(prev => new Set([...prev, insightId]));
        toast.success('Hinweis abgehakt');
      }
    } catch (error) {
      console.error('Dismiss error:', error);
    }
  };

  useEffect(() => {
    fetchAssistant();
  }, [fetchAssistant]);

  const getWeatherIcon = (type) => {
    switch(type) {
      case 'rainy': return <CloudRain className="w-5 h-5 text-blue-400" />;
      case 'snowy': return <CloudSnow className="w-5 h-5 text-blue-200" />;
      case 'sunny': return <Sun className="w-5 h-5 text-yellow-400" />;
      case 'cloudy': return <Cloud className="w-5 h-5 text-gray-400" />;
      default: return <Thermometer className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTimeIcon = () => {
    const hour = new Date().getHours();
    if (hour < 12) return <Sunrise className="w-5 h-5 text-amber-400" />;
    if (hour < 18) return <Sun className="w-5 h-5 text-yellow-400" />;
    return <Moon className="w-5 h-5 text-blue-400" />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen" data-testid="tagesassistent-loading">
        <div className="text-center">
          <Bot className="w-16 h-16 animate-pulse mx-auto mb-4 text-purple-400" />
          <p className="text-gray-400 text-lg">Ihr KI-Tagesassistent analysiert...</p>
          <p className="text-gray-500 text-sm mt-2">Wetter, Feiertage, Bestellungen...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <p className="text-gray-400">Tagesassistent nicht verfügbar</p>
          <Button className="mt-4" onClick={() => fetchAssistant()}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  const visibleInsights = data.insights?.filter(i => !dismissedIds.has(i.insight_id)) || [];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="tagesassistent-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">KI-Tagesassistent</h1>
            <p className="text-gray-400 text-sm">Ihr digitaler Betriebsleiter mit Gastro-Wissen</p>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          onClick={() => fetchAssistant(false)}
          disabled={refreshing}
          data-testid="refresh-btn"
          className="border-slate-600"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Kontext-Leiste (Wetter, Zeit, Feiertag) */}
      <div className="flex flex-wrap gap-3 mb-6">
        {data.context?.weather && (
          <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-slate-700">
            {getWeatherIcon(data.context.weather.type)}
            <span className="text-white font-medium">{data.context.weather.temp}°C</span>
            <span className="text-gray-400 text-sm">{data.context.weather.description}</span>
          </div>
        )}
        
        <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-slate-700">
          {getTimeIcon()}
          <span className="text-white">{data.context?.weekday}</span>
          <span className="text-gray-400">• {data.context?.period}</span>
        </div>
        
        {data.context?.is_holiday && (
          <div className="flex items-center gap-2 bg-amber-500/10 px-4 py-2 rounded-full border border-amber-500/30">
            <Calendar className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-medium">{data.context.holiday_name}</span>
          </div>
        )}
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30 mb-6">
        <CardContent className="py-5">
          <div className="flex items-start gap-4">
            <Sparkles className="w-8 h-8 text-purple-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-xl text-white font-medium mb-2">{data.summary}</p>
              <p className="text-sm text-gray-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Stand: {new Date(data.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Kritisch', value: data.stats.critical, color: 'red', show: data.stats.critical > 0 },
          { label: 'Warnungen', value: data.stats.warnings, color: 'amber', show: true },
          { label: 'Positive', value: data.stats.success, color: 'green', show: true },
          { label: 'Hinweise', value: data.stats.info, color: 'blue', show: true },
          { label: 'Aktionen', value: data.stats.actionable, color: 'purple', show: data.stats.actionable > 0 }
        ].filter(s => s.show).map((stat, idx) => (
          <Card key={idx} className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-3 px-4">
              <div className="text-center">
                <p className={`text-2xl font-bold text-${stat.color}-400`}>{stat.value}</p>
                <p className="text-xs text-gray-400">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gastro-Tipp des Tages */}
      {data.gastro_tip && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-400 font-medium text-sm">{data.gastro_tip.title}</p>
                <p className="text-white">{data.gastro_tip.tip}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {visibleInsights.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
            <h3 className="text-xl font-medium text-white mb-2">Alles im grünen Bereich!</h3>
            <p className="text-gray-400">
              Heute gibt es keine besonderen Hinweise. Weiter so!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleInsights.map((insight, idx) => (
            <Card 
              key={insight.insight_id || idx}
              className={`border transition-all hover:scale-[1.01] ${SEVERITY_STYLES[insight.severity]}`}
              data-testid={`insight-${idx}`}
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Icon */}
                    <div className="text-3xl flex-shrink-0">{insight.icon}</div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-white text-lg">{insight.title}</h3>
                        <Badge variant="outline" className={`text-xs ${SEVERITY_BADGE_STYLES[insight.severity]}`}>
                          {insight.category === 'revenue' ? 'Umsatz' :
                           insight.category === 'products' ? 'Produkte' :
                           insight.category === 'customers' ? 'Kunden' :
                           insight.category === 'operations' ? 'Betrieb' :
                           insight.category === 'portals' ? 'Portale' :
                           insight.category}
                        </Badge>
                      </div>
                      
                      <p className="text-gray-300">{insight.message}</p>
                      
                      {/* Kontext - das Gastro-Wissen */}
                      {insight.context && (
                        <p className="text-sm text-purple-400 mt-2 flex items-start gap-2">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          {insight.context}
                        </p>
                      )}
                      
                      {/* Data Source */}
                      <p className="text-xs text-gray-600 mt-2">
                        📊 {insight.data_source}
                      </p>
                      
                      {/* Action Button */}
                      {insight.actionable && insight.action_text && (
                        <Button 
                          size="sm" 
                          className="mt-3 bg-purple-600 hover:bg-purple-700"
                          onClick={() => {
                            if (insight.action_link) {
                              window.location.href = insight.action_link;
                            }
                          }}
                        >
                          {insight.action_text}
                          {insight.action_link ? <ExternalLink className="w-3 h-3 ml-2" /> : <ChevronRight className="w-4 h-4 ml-1" />}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Dismiss Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissInsight(insight.insight_id)}
                    className="text-gray-500 hover:text-white flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Footer */}
      <Card className="bg-slate-800/30 border-slate-700/50 mt-6">
        <CardContent className="py-4">
          <div className="flex items-center gap-3 text-center justify-center">
            <Bot className="w-5 h-5 text-purple-400" />
            <p className="text-sm text-gray-400">
              <span className="text-white font-medium">{data.principle?.title}</span>
              {" – "}
              {data.principle?.description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
